import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import brandLogo from "@/assets/alchemist-logo-white.png";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Landing() {
  const { user, setUser, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isRegister = location.pathname === "/register";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!loading && user) navigate("/dashboard", { replace: true });
  }, [user, loading, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const endpoint = isRegister ? "/auth/register" : "/auth/login";
      const body = isRegister ? { email, password, name } : { email, password };
      const res = await api.post(endpoint, body);
      setUser(res.data.user);
      toast.success(isRegister ? "Welcome to Alchemist Lab." : "Welcome back.");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      const msg = formatApiErrorDetail(err?.response?.data?.detail) || err.message;
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#030303] text-white relative overflow-hidden flex flex-col">
      <div
        className="absolute inset-0 opacity-40"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1761875002732-5140ada50d2a?crop=entropy&cs=srgb&fm=jpg&w=2400&q=85')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(3,3,3,0.2) 0%, rgba(3,3,3,0.85) 65%, #030303 100%)",
        }}
      />

      <section className="relative z-10 flex-1 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md">
          <div className="flex flex-col items-center mb-10">
            <img
              src={brandLogo}
              alt="Alchemist Lab"
              className="h-12 md:h-14 w-auto select-none"
              data-testid="brand-logo-landing"
              draggable={false}
            />
            <h1 className="font-display text-4xl md:text-5xl tracking-tight mt-8" data-testid="landing-title">Alchemist Lab</h1>
          </div>
          <div className="panel p-8 md:p-10 backdrop-blur-sm bg-[#0A0A0A]/80">
            <h2 className="font-display text-2xl tracking-tight mb-8">
              {isRegister ? "Create your account" : "Sign in"}
            </h2>
            <form onSubmit={onSubmit} className="space-y-6" data-testid={isRegister ? "register-form" : "login-form"}>
              {isRegister && (
                <div>
                  <div className="label-mono mb-2">Artist name</div>
                  <input
                    type="text"
                    className="mp-input"
                    placeholder="e.g. Anyma"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    data-testid="register-name-input"
                    autoComplete="name"
                  />
                </div>
              )}
              <div>
                <div className="label-mono mb-2">Email</div>
                <input
                  type="email"
                  className="mp-input"
                  placeholder="you@yourdomain.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid={isRegister ? "register-email-input" : "login-email-input"}
                  autoComplete="email"
                />
              </div>
              <div>
                <div className="label-mono mb-2">Password</div>
                <input
                  type="password"
                  className="mp-input"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  data-testid={isRegister ? "register-password-input" : "login-password-input"}
                  autoComplete={isRegister ? "new-password" : "current-password"}
                />
              </div>
              {error && (
                <div className="text-[#FF2A2A] text-sm label-mono" data-testid="auth-error">
                  {error}
                </div>
              )}
              <button
                type="submit"
                className="btn-amber w-full justify-center disabled:opacity-60"
                disabled={submitting}
                data-testid={isRegister ? "register-submit" : "login-submit"}
              >
                {submitting ? (isRegister ? "Creating…" : "Signing in…") : (isRegister ? "Create account" : "Sign in")}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
